import { PopoverTabIndex } from '@/Enums/FilterPopoverTabs.enum';
import { AdvertisersTabButton, ClientsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { BASE_TAB_ID } from '@/constants/firstPartyData';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import AddFilterButton from './AddFilterButton';

const FilterPopover = ({ filters, setFilters, clients, loadingClients }) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [currentTab, setCurrentTab] = useState(PopoverTabIndex.Clients);

  const [pendingClients, setPendingClients] = useState(filters.clients);
  const [pendingAdvertisers, setPendingAdvertisers] = useState(filters.advertisers);

  const resetPendingValues = useCallback(() => {
    setPendingClients(filters.clients);
    setPendingAdvertisers(filters.advertisers);
  }, [filters.advertisers, filters.clients]);

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
  };

  const handleOpen = ({ currentTarget }) => {
    resetPendingValues();
    setFilterAnchorEl(currentTarget);
  };

  const handleClose = () => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(PopoverTabIndex.Clients);
  };

  const handleSubmit = () => {
    if (
      areArraysOfObjectsEqual(filters.clients, pendingClients) &&
      areArraysOfObjectsEqual(filters.advertisers, pendingAdvertisers)
    ) {
      handleClose();
      return;
    }
    setFilters({
      clients: pendingClients,
      advertisers: pendingAdvertisers,
    });
    handleClose();
  };

  const updateClientsValue = (updatedClients = []) => {
    setPendingClients(updatedClients);
    setPendingAdvertisers([]);
  };

  const updateAdvertisersValue = (updatedAdvertisers = []) => {
    setPendingAdvertisers(updatedAdvertisers);
  };

  const filtersOpen = Boolean(filterAnchorEl);
  const filtersId = filtersOpen ? 'filters-popover' : undefined;

  const advertisers = useMemo(() => {
    return (pendingClients || [])
      .map(client => client.advertisers)
      .flat()
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [pendingClients]);

  return (
    <>
      <AddFilterButton onClick={handleOpen} describedBy={filtersId} variant='outlined' />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='pacing filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          <Tab component={ClientsTabButton} {...a11yProps(PopoverTabIndex.Clients, BASE_TAB_ID)} />
          <Tab component={AdvertisersTabButton} {...a11yProps(PopoverTabIndex.Advertisers, BASE_TAB_ID)} />
        </Tabs>
        <TabPanel className='w-full' baseTabId={BASE_TAB_ID} value={currentTab} index={PopoverTabIndex.Clients}>
          <TabSelect
            label='Client'
            loading={loadingClients}
            options={clients}
            value={pendingClients}
            onChange={updateClientsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel className='w-full' baseTabId={BASE_TAB_ID} value={currentTab} index={PopoverTabIndex.Advertisers}>
          <TabSelect
            label='Advertiser'
            loading={loadingClients}
            options={advertisers}
            value={pendingAdvertisers}
            onChange={updateAdvertisersValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

FilterPopover.propTypes = {
  filters: PropTypes.object,
  setFilters: PropTypes.func,
  clients: PropTypes.array,
  loadingClients: PropTypes.bool,
};

export default FilterPopover;
